import React, { useEffect, useState } from 'react';
import { colors } from '../styles/colors';
import { Download } from 'lucide-react';

const WrittenContent1Page = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    setIsVisible(true);
    window.scrollTo(0, 0);
    // Intentional: mount-only effect; deps intentionally omitted
  }, []);

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.black }}>
      {/* Main Content */}
      <article className="pt-24 sm:pt-28 pb-16 sm:pb-20">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          
          {/* Page Title */}
          <h1 
            className={`text-3xl sm:text-4xl md:text-5xl font-bold mb-8 sm:mb-10 leading-tight transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ color: colors.white }}
          >
            Understanding Arizore: Bridging Physical Assets with Digital Finance
          </h1>

          {/* Hero Image */}
          <div 
            className={`mb-8 sm:mb-10 transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '100ms' }}
          >
            <img 
              src="/written-content-1-hero.png" 
              alt="Understanding Arizore" 
              className="w-full rounded-lg"
            />
          </div>

          {/* Contextual Framing */}
          <p 
            className={`text-sm sm:text-base mb-10 sm:mb-12 pb-8 sm:pb-10 border-b transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ 
              color: colors.white, 
              opacity: 0.5, 
              borderColor: `${colors.white}15`,
              transitionDelay: '200ms'
            }}
          >
            This document is part of Arizore's Written Content series — internal sense-making work shared publicly to explain how the reserve ecosystem functions under real-world conditions. These pieces are designed to explore structure, risk, and discipline, not to market products.
          </p>

          {/* Article Content */}
          <div 
            className={`prose prose-lg max-w-none transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ transitionDelay: '300ms' }}
          >
            {/* Introduction */}
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Mining holds vast value locked underground, yet banks see only dirt when miners seek loans. Arizore tackles this gap by creating infrastructure that turns physical assets into credible digital finance. But this system isn't built on hope, it's driven by strict eligibility and ruthless discipline to separate real value from risk. Let's unpack how Arizore makes this complex bridge work, especially when everything can go wrong.
            </p>

            {/* Section: Introduction to Arizore */}
            <h2 style={{ color: colors.white, fontSize: '1.5rem', fontWeight: '600', marginTop: '2.5rem', marginBottom: '1rem' }}>
              Introduction to Arizore
            </h2>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              When it comes to mining, it's not just about extracting resources. It's about finding a pathway that bridges physical assets with the world of digital finance. Arizore is designed to make this transition seamless and credible, ensuring that every step is backed by a robust framework and strict guidelines.
            </p>

            {/* Section: Bridging Physical and Digital Assets */}
            <h2 style={{ color: colors.white, fontSize: '1.5rem', fontWeight: '600', marginTop: '2.5rem', marginBottom: '1rem' }}>
              Bridging Physical and Digital Assets
            </h2>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Imagine you're sitting on a goldmine, but the banks only see dirt. This is where Arizore steps in, turning tangible assets into digital credibility. With a structured reserve system, Arizore helps miners and bullion providers unlock value that traditional financial institutions often overlook. This isn't just about technology; it's about creating a bridge that connects the real world with the digital, offering new opportunities for growth and partnership. Here's how it works: miners pledge their assets, and Arizore transforms these pledges into digital tokens that represent real value. This enables miners to access funding without sacrificing ownership.
            </p>

            {/* Section: Understanding the Core Challenges */}
            <h2 style={{ color: colors.white, fontSize: '1.5rem', fontWeight: '600', marginTop: '2.5rem', marginBottom: '1rem' }}>
              Understanding the Core Challenges
            </h2>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              One might wonder why such a system is necessary. The answer lies in the challenges miners face when trying to secure funding. Many banks view untapped resources as speculative, making it difficult for miners to leverage their full potential. Arizore addresses this by providing a transparent and compliant pathway that instills confidence in investors. This approach not only mitigates risks but also opens up new avenues for financial growth. The key is in Arizore's ability to distinguish between real value and potential risk, ensuring that only credible assets are tokenized and monetized.
            </p>

            {/* Section: The Lifecycle of a Mine */}
            <h2 style={{ color: colors.white, fontSize: '1.5rem', fontWeight: '600', marginTop: '2.5rem', marginBottom: '1rem' }}>
              The Lifecycle of a Mine
            </h2>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              From the moment a mine starts operations to its peak production, the journey is fraught with challenges. Arizore understands this and offers a solution that transforms desperation into opportunity.
            </p>

            {/* Section: From Desperation to Opportunity */}
            <h3 style={{ color: colors.white, fontSize: '1.25rem', fontWeight: '600', marginTop: '2rem', marginBottom: '0.75rem' }}>
              From Desperation to Opportunity
            </h3>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              When a miner first approaches Arizore, they are often in a state of financial need. Traditional banks may not see the potential in untapped resources, but Arizore does. By converting physical assets into digital tokens, Arizore provides miners with the ability to access funds without losing control over their resources. This approach is not only innovative but also essential in transforming a miner's financial landscape. The tokens represent a share of the pledged resources, giving miners a chance to secure the necessary capital to start or continue operations.
            </p>

            {/* Section: Eligibility and Verification Processes */}
            <h3 style={{ color: colors.white, fontSize: '1.25rem', fontWeight: '600', marginTop: '2rem', marginBottom: '0.75rem' }}>
              Eligibility and Verification Processes
            </h3>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              The cornerstone of Arizore's model is its strict eligibility and verification process. Miners must prove their resources through verified reports and geological assessments. This ensures that only genuine assets are tokenized. Arizore's rigorous standards filter out speculative ventures, focusing on measured and indicated resources. This not only protects the reserve's integrity but also builds trust among investors and partners. By adhering to these standards, Arizore creates a secure environment where both miners and investors can thrive.
            </p>

            {/* Section: Structuring the Financial Ecosystem */}
            <h2 style={{ color: colors.white, fontSize: '1.5rem', fontWeight: '600', marginTop: '2.5rem', marginBottom: '1rem' }}>
              Structuring the Financial Ecosystem
            </h2>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Arizore's financial ecosystem is structured to ensure stability and growth. Through strategic implementation, it balances risk and reward, providing a solid foundation for all stakeholders.
            </p>

            {/* Section: Implementing Haircuts and LTV Ratios */}
            <h3 style={{ color: colors.white, fontSize: '1.25rem', fontWeight: '600', marginTop: '2rem', marginBottom: '0.75rem' }}>
              Implementing Haircuts and LTV Ratios
            </h3>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              When it comes to valuing resources, Arizore applies haircuts to account for various risks and challenges. These haircuts range from 25% to 55%, depending on the region and resource type. The Loan-to-Value (LTV) ratios are also conservative, ensuring that miners remain committed to delivering their pledged resources. By applying these measures, Arizore minimizes risk and encourages responsible behavior. This strategy not only safeguards the reserve but also aligns the interests of miners and investors, fostering long-term partnerships.
            </p>

            {/* Section: Tokens and Capital Access Strategy */}
            <h3 style={{ color: colors.white, fontSize: '1.25rem', fontWeight: '600', marginTop: '2rem', marginBottom: '0.75rem' }}>
              Tokens and Capital Access Strategy
            </h3>
            <p style={{ color: colors.white, opacity: 0.85, marginBottom: '1.5rem', lineHeight: '1.8' }}>
              Tokens play a crucial role in Arizore's ecosystem. They provide miners with a new way to access capital while maintaining ownership of their resources. Unlike traditional methods, these tokens can be used as collateral for secured lending, enabling miners to fund operations without diluting their equity. This approach shifts the focus from short-term gains to sustainable growth. It encourages miners to deliver on their promises, building a track record of trust and reliability. In turn, this strengthens the overall ecosystem, benefiting all participants.
            </p>

            {/* Conclusion */}
            <p style={{ color: colors.white, opacity: 0.85, marginTop: '2rem', marginBottom: '1.5rem', lineHeight: '1.8' }}>
              By offering a transparent and disciplined approach, Arizore empowers miners, bullion providers, and investors to explore new financial opportunities. Through its innovative framework, it bridges the gap between physical assets and digital finance, creating a resilient ecosystem built on trust and collaboration.
            </p>
          </div>

          {/* PDF Download */}
          <div 
            className={`mt-12 sm:mt-16 pt-8 sm:pt-10 border-t transition-all duration-1000 transform ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
            }`}
            style={{ borderColor: `${colors.white}15`, transitionDelay: '400ms' }}
          >
            <a
              href="/written-content-1.pdf"
              download="Understanding-Arizore-Bridging-Physical-Assets-with-Digital-Finance.pdf"
              className="inline-flex items-center gap-3 px-5 py-3 rounded-lg text-sm font-medium transition-all duration-200"
              style={{ 
                backgroundColor: `${colors.white}10`,
                color: colors.white,
                opacity: 0.8
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = `${colors.white}15`;
                e.currentTarget.style.opacity = 1;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = `${colors.white}10`;
                e.currentTarget.style.opacity = 0.8;
              }}
            >
              <Download className="w-4 h-4" />
              Download PDF
            </a>
          </div>

        </div>
      </article>
    </div>
  );
};

export default WrittenContent1Page;
